import { useEmailFieldState, useRequiredFieldState } from '@cashew/hooks';
import { api } from '@cashew/utils';
import React, {
  useEffect,
  useState,
  useCallback,
  ChangeEvent,
  useMemo,
} from 'react';

import { useTranslation } from 'react-i18next';
import Input from '@cashew/input';
import {
  countries,
  countriesAsStrings,
  Country,
  DefaultSectionProps,
  isCountry,
  isStringUnknownRecord,
  merchantEndpoint,
  useToasterErrorState,
  useToasterSuccessState,
} from './util';
import OnboardingForm from './OnboardingForm';
import SelectComponent from '../../components/SelectComponent';

export interface MerchantInfo {
  companyCountry: Country;
  companyName: string;
  representativeFirstName: string;
  representativeLastName: string;
  representativeContractNumber: string;
  representativeEmail: string;
}

interface DataWithMerchantInfo {
  data: MerchantInfo;
}

export const hasDataWithMerchantInfo = (
  entry: unknown
): entry is DataWithMerchantInfo =>
  isStringUnknownRecord(entry) &&
  isStringUnknownRecord(entry.data) &&
  typeof entry.data.companyCountry === 'string' &&
  countriesAsStrings.includes(entry.data.companyCountry) &&
  typeof entry.data.companyName === 'string' &&
  typeof entry.data.representativeFirstName === 'string' &&
  typeof entry.data.representativeLastName === 'string' &&
  typeof entry.data.representativeContractNumber === 'string' &&
  typeof entry.data.representativeEmail === 'string';

/**
 * Orders Page RefundPage
 */

const MerchantSection = ({
  merchantData,
  isLoading,
  setIsSectionValid,
}: DefaultSectionProps) => {
  const { t } = useTranslation();

  const [isSaving, setIsSaving] = useState(false);
  const [ErrorToaster, setToasterError] = useToasterErrorState(setIsSaving);
  const [SuccessToaster, setToasterSuccess] = useToasterSuccessState();
  const [country, setCountry, countryError] = useRequiredFieldState<Country>(
    merchantData?.companyCountry ?? '',
    t
  );
  const [companyName, setCompanyName, companyNameError] = useRequiredFieldState(
    merchantData?.companyName ?? '',
    t
  );
  const [firstName, setFirstName, firstNameError] = useRequiredFieldState(
    merchantData?.representativeFirstName ?? '',
    t
  );
  const [lastName, setLastName, lastNameError] = useRequiredFieldState(
    merchantData?.representativeLastName ?? '',
    t
  );
  const [contractNumber, setContactNumber, contractNumberError] =
    useRequiredFieldState(merchantData?.representativeContractNumber ?? '', t);
  const [emailAddress, setEmailAddress, emailAddressError] = useEmailFieldState(
    merchantData?.representativeEmail ?? '',
    t
  );

  useEffect(() => {
    setCountry(merchantData?.companyCountry ?? '');
    setCompanyName(merchantData?.companyName ?? '');
    setFirstName(merchantData?.representativeFirstName ?? '');
    setLastName(merchantData?.representativeLastName ?? '');
    setContactNumber(merchantData?.representativeContractNumber ?? '');
    setEmailAddress(merchantData?.representativeEmail ?? '');
  }, [merchantData]);

  const countryOnSelect = useCallback(
    (key: string) => {
      if (isCountry(key)) {
        setCountry(key);
        return;
      }
      setCountry('');
    },
    [setCountry]
  );
  const countryDropdownData = useMemo(
    () =>
      countries.map((key) => ({
        key,
        value: key || t('merchantOnboarding.merchant.field.country'),
      })),
    [t]
  );
  const companyNameOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setCompanyName(e.target.value),
    [setCompanyName]
  );
  const firstNameOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value),
    [setFirstName]
  );
  const lastNameOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setLastName(e.target.value),
    [setLastName]
  );
  const contractNumberOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setContactNumber(e.target.value),
    [setContactNumber]
  );
  const emailAddressOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setEmailAddress(e.target.value),
    [setEmailAddress]
  );

  const haveErrors = useMemo(
    () =>
      countryError.hasError ||
      companyNameError.hasError ||
      firstNameError.hasError ||
      lastNameError.hasError ||
      contractNumberError.hasError ||
      emailAddressError.hasError,
    [
      countryError.hasError,
      companyNameError.hasError,
      firstNameError.hasError,
      lastNameError.hasError,
      contractNumberError.hasError,
      emailAddressError.hasError,
    ]
  );

  const saveOnClick = useCallback(() => {
    if (!isLoading && merchantData) {
      setIsSaving(true);
      api
        .patch({
          endpoint: merchantEndpoint(`${merchantData.id}`),
          params: {
            companyCountry: country,
            companyName,
            representativeFirstName: firstName,
            representativeLastName: lastName,
            representativeContractNumber: contractNumber,
            representativeEmail: emailAddress,
          },
        })
        .then(() => {
          setIsSaving(false);
          setToasterSuccess(
            t('onboardingForm.success.title.saving'),
            t('onboardingForm.success.message.saving')
          );
        })
        .catch((error: any) =>
          setToasterError(t('onboardingForm.error.title.saving'), error)
        );
    }
  }, [
    haveErrors,
    country,
    companyName,
    firstName,
    lastName,
    contractNumber,
    emailAddress,
    setToasterSuccess,
  ]);

  useEffect(() => setIsSectionValid(!haveErrors), [haveErrors]);

  return (
    <OnboardingForm
      saveDisabled={haveErrors}
      isSaving={isSaving}
      saveOnClick={saveOnClick}
    >
      {(formClassname) => (
        <>
          {/* ts-ignore */}
          <ErrorToaster />
          <SuccessToaster />
          <div className={`${formClassname}__line`}>
            <strong className={`${formClassname}__label-text`}>
              {t('merchantOnboarding.merchant.section.merchantDetails')}
            </strong>
          </div>
          <div className={`${formClassname}__line`}>
            <div>
              <SelectComponent
                label={t('merchantOnboarding.merchant.field.country')}
                data={countryDropdownData}
                selected={country}
                onSelect={countryOnSelect}
                error={countryError.message as string}
              />
            </div>
            <div>
              <Input
                placeholder={t('merchantOnboarding.merchant.field.companyName')}
                name="companyName"
                value={companyName}
                onChange={companyNameOnChange}
                error={companyNameError.message as string}
              />
            </div>
          </div>
          <div className={`${formClassname}__separator`} />
          <div className={`${formClassname}__line`}>
            <strong className={`${formClassname}__label-text`}>
              {t('merchantOnboarding.merchant.section.merchantRepresentative')}
            </strong>
          </div>
          <div className={`${formClassname}__line`}>
            <div className={`${formClassname}__col`}>
              <Input
                placeholder={t('merchantOnboarding.merchant.field.firstName')}
                name="firstName"
                value={firstName}
                onChange={firstNameOnChange}
                error={firstNameError.message as string}
              />
            </div>
            <div>
              <Input
                placeholder={t('merchantOnboarding.merchant.field.lastName')}
                name="lastName"
                value={lastName}
                onChange={lastNameOnChange}
                error={lastNameError.message as string}
              />
            </div>
          </div>
          <div className={`${formClassname}__line`}>
            <div className={`${formClassname}__col`}>
              <Input
                placeholder={t(
                  'merchantOnboarding.merchant.field.contractNumber'
                )}
                name="contractNumber"
                value={contractNumber}
                onChange={contractNumberOnChange}
                error={contractNumberError.message as string}
              />
            </div>
            <div>
              <Input
                placeholder={t(
                  'merchantOnboarding.merchant.field.emailAddress'
                )}
                name="email"
                value={emailAddress}
                onChange={emailAddressOnChange}
                error={emailAddressError.message as string}
              />
            </div>
          </div>
        </>
      )}
    </OnboardingForm>
  );
};

export default MerchantSection;
